import * as R from 'ramda'
import React, { useState } from 'react'
import { UseFormReturn } from 'react-hook-form'


import { rejectNil } from '@unimpaired/utils'


import {
  Button,
  Select,
  SelectProps,
} from '../../../component-exports'
import { pipe } from 'fp-ts/lib/function'
import { Country } from 'country-state-city'
import { TZDate } from '@date-fns/tz'

export type ZoneSelectFieldProps = Omit<
  SelectProps,
  'defaultValues' | 'name' | 'label' | 'children' | 'form'
> & {
  form: UseFormReturn<any>
  name?: string
  label?: string
  className?: string
  defaultValue?: string
}

const currentTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone
const countries = Country.getAllCountries()
const currentCountry = pipe(
  countries,
  R.find(
    (country) =>
      !!country.timezones?.find((tz) => tz.zoneName === currentTimeZone),
  ),
)

const getTimeZonesForCountry = (allCountries?: boolean) =>
  pipe(
    countries,
    R.ifElse(
      R.always(!!allCountries),
      R.always(countries),
      R.find(R.equals(currentCountry)),
    ),
    R.defaultTo(countries),
    (countries) => (Array.isArray(countries) ? countries : [countries]),
    R.map((country) => country.timezones),
    rejectNil,
    R.flatten,
    R.uniqBy(R.prop('tzName')),
    R.map((tz) => ({
      tzName: new TZDate(new Date(), tz.zoneName)
        .toLocaleDateString(undefined, { day: '2-digit', timeZoneName: 'long' })
        .substring(4),
      tzAbbr: new TZDate(new Date(), tz.zoneName)
        .toLocaleDateString(undefined, {
          day: '2-digit',
          timeZoneName: 'short',
        })
        .substring(4),
      zoneName: tz.zoneName,
    })),
  )

export const ZoneSelectField = ({
  name = 'timezone',
  defaultValue,
  ...props
}: ZoneSelectFieldProps) => {
  const [showAllCountries, setShowAllCountries] = useState(false)
  const [selectableTimezones, setSelectableTimezones] = useState(
    getTimeZonesForCountry(),
  )
  !props.form.watch(name) &&
    props.form.setValue(name, defaultValue ?? currentTimeZone, {
      shouldValidate: true,
    })
  return (
    <>
      <Select
        {...props}
        form={props.form}
        label={props.label ?? 'Timezone'}
        name={name}
        search
        keepMenuOnAppendedElementClick
        appendElementToDropDown={
          <ToggleAllTimezonesButton
            showAllCountries={showAllCountries}
            onClick={() => {
              setSelectableTimezones(getTimeZonesForCountry(showAllCountries))
              setShowAllCountries((prev) => !prev)
            }}
          />
        }>
        {selectableTimezones.map((t) => (
          <Select.Option label={t.tzName} value={t.zoneName}>
            {t.tzName} ({t.tzAbbr}) ({t.zoneName})
          </Select.Option>
        ))}
      </Select>
    </>
  )
}

const ToggleAllTimezonesButton = ({
  showAllCountries,
  onClick,
}: {
  showAllCountries: boolean
  onClick: () => void
}) => (
  <Button onClick={onClick} className='w-full'>
    {showAllCountries ? 'Show Timezones For My Country' : 'Show All Timezones'}
  </Button>
)
